import React, { FC } from 'react';

import { BaseProgress } from './BaseProgress';

import { Text } from '@/shared/ui/Text';
import { cn } from '@/shared/lib/tailwind-merge';

export enum ProgressSize {
    Small = 'small',
    Default = 'default',
    Large = 'large',
}

export enum ProgressColor {
    Default = '#5030E5',
    Secondary = '#000000',
}

interface ProgressProps {
    percent: number;
    size?: ProgressSize;
    strokeColor?: string;
    trailColor?: string;
    withProgress?: boolean;
    withDetails?: boolean;
    rounded?: boolean;
    width?: number;
}

interface ProgressComponent extends FC<ProgressProps> {
    sizes: typeof ProgressSize;
    colors: typeof ProgressColor;
}

const Progress: ProgressComponent = ({
    percent,
    size = ProgressSize.Default,
    trailColor,
    withProgress,
    withDetails,
    rounded = true,
    width,
}) => {
    const heightStyles = {
        [ProgressSize.Small]: 'h-1',
        [ProgressSize.Default]: 'h-2',
        [ProgressSize.Large]: 'h-3',
    };

    const detailsSeparatorStyles = {
        [ProgressSize.Small]: 'h-3 w-1 top-[-3px] right-0',
        [ProgressSize.Default]: 'h-3 w-1 top-[-2px] right-0',
        [ProgressSize.Large]: 'h-5 w-1 top-[-4px] right-0',
    };

    return (
        <div className='flex flex-row items-center'>
            <BaseProgress
                className={cn(`relative bg-background-secondary`, heightStyles[size], {
                    'rounded-full': rounded,
                })}
                style={{
                    backgroundColor: trailColor,
                    width: width ? `${width}px` : '100%',
                }}
            >
                <div
                    className={cn('absolute left-0 h-full transition-all', {
                        'rounded-full': rounded,
                    })}
                    style={{
                        width: `${percent}%`,
                        backgroundColor: '#5030E5',
                        maxWidth: '100%',
                    }}
                >
                    {withDetails && (
                        <>
                            <div
                                className={cn(
                                    'absolute bg-background-alwaysDark mb-1',
                                    detailsSeparatorStyles[size],
                                )}
                            />
                            <div className='absolute right-[-20px] top-[-24px] flex flex-col items-center'>
                                <Text type={Text.types.Label} className='font-semibold text-black'>
                                    {percent.toFixed(2)}
                                </Text>
                            </div>
                        </>
                    )}
                </div>
            </BaseProgress>
            {withProgress && (
                <Text type={Text.types.Label} className='ml-4 font-semibold'>
                    {percent}%
                </Text>
            )}
        </div>
    );
};

Progress.sizes = ProgressSize;
Progress.colors = ProgressColor;

export { Progress };
